import { useState } from 'react';
import { useTenants } from '../../../../components/tenant-selector/useTenants';
import { useAutomationCategories } from '../../automation-categories/queries';
import { useFeatureSurface } from '../../FeatureSurfaceContext';
import { templateMatchesFeature } from '../../featureSurfaceConfig';
import type { AutomationTemplate } from '../templates.types';
import { useAutomationTemplates } from '../../useAutomationTemplates';
import {
  deriveTemplateStatus,
  resolveActiveBusinessIds,
  resolveSyncedBusinessIds,
} from './templateStatus';
import {
  StatusFilter,
  type SortDirection,
  type TemplateListRow,
  type TemplateListStatus,
  type TemplateListView,
  type TemplateSortKey,
  type TenantFilterOption,
} from './types';

const STATUS_SORT_RANK: Record<TemplateListStatus, number> = {
  [StatusFilter.Draft]: 0,
  [StatusFilter.Available]: 1,
  [StatusFilter.Active]: 2,
};

const templateDisplayName = (template: AutomationTemplate): string =>
  template.contents?.name || template.name;

const compareRows = (
  a: TemplateListRow,
  b: TemplateListRow,
  sortKey: TemplateSortKey,
  sortDirection: SortDirection,
): number => {
  let primary = 0;
  if (sortKey === 'name') {
    primary = a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase());
  } else if (sortKey === 'tenantsUsing') {
    primary = a.templateSubscriberCount - b.templateSubscriberCount;
  } else {
    primary = STATUS_SORT_RANK[a.status] - STATUS_SORT_RANK[b.status];
  }

  const comparison =
    primary === 0 ? a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()) : primary;
  return sortDirection === 'asc' ? comparison : comparison * -1;
};

/**
 * Client-side search / status filter / tenant filter / sort for the partner automation-templates
 * list. Fetches the templates and tenant roster itself; status and per-tenant counts derive from
 * `targetedTenantsFilter` + the `activeBusinessIds` the list API returns, resolved against the
 * live tenant roster.
 */
export function useTemplateListView(): TemplateListView {
  const { data: templatesData, isLoading: isTemplatesLoading, error } = useAutomationTemplates();
  const featureSurface = useFeatureSurface();
  const { data: tenants, isLoading: isTenantsLoading } = useTenants();
  const { data: categories = [] } = useAutomationCategories();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(StatusFilter.All);
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<TemplateSortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const templates = (templatesData?.templates ?? []).filter(template =>
    templateMatchesFeature(template, featureSurface.feature),
  );
  const roster = tenants ?? [];
  const rosterBusinessIds = roster.map(tenant => tenant.id);
  const tenantNameById = new Map(roster.map(tenant => [tenant.id, tenant.name]));

  const allRows: TemplateListRow[] = templates.map(template => {
    const activeBusinessIds = resolveActiveBusinessIds(template, rosterBusinessIds);
    return {
      template,
      displayName: templateDisplayName(template),
      description: template.contents?.description || '',
      status: deriveTemplateStatus(template),
      templateSubscriberCount: activeBusinessIds.length,
      activeBusinessIds,
      categories: template.categories ?? [],
    };
  });

  const syncedBusinessIdsByTemplateId = new Map(
    templates.map(template => [template.id, resolveSyncedBusinessIds(template, rosterBusinessIds)]),
  );

  const statusCounts: Record<StatusFilter, number> = {
    [StatusFilter.All]: allRows.length,
    [StatusFilter.Draft]: 0,
    [StatusFilter.Available]: 0,
    [StatusFilter.Active]: 0,
  };
  for (const row of allRows) {
    statusCounts[row.status] += 1;
  }

  const tenantFilterOptions: TenantFilterOption[] = roster
    .map(tenant => ({
      id: tenant.id,
      name: tenant.name,
      templateCount: templates.reduce(
        (count, template) =>
          syncedBusinessIdsByTemplateId.get(template.id)?.includes(tenant.id) ? count + 1 : count,
        0,
      ),
    }))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const normalizedSearch = search.trim().toLowerCase();
  const selectedTenantIdSet = new Set(selectedTenantIds);
  const selectedCategoryIdSet = new Set(selectedCategoryIds);

  const filteredRows = allRows.filter(row => {
    const matchesSearch =
      normalizedSearch.length === 0 || row.displayName.toLowerCase().includes(normalizedSearch);
    const matchesStatus = statusFilter === StatusFilter.All || row.status === statusFilter;
    const syncedIds = syncedBusinessIdsByTemplateId.get(row.template.id) ?? [];
    const matchesTenant =
      selectedTenantIds.length === 0 || syncedIds.some(id => selectedTenantIdSet.has(id));
    const matchesCategory =
      selectedCategoryIds.length === 0 ||
      row.categories.some(category => selectedCategoryIdSet.has(category.id));
    return matchesSearch && matchesStatus && matchesTenant && matchesCategory;
  });

  const rows = [...filteredRows].sort((a, b) => compareRows(a, b, sortKey, sortDirection));

  const toggleSort = (key: TemplateSortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection(key === 'name' ? 'asc' : 'desc');
      return;
    }
    setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
  };

  const toggleTenant = (tenantId: string) =>
    setSelectedTenantIds(current =>
      current.includes(tenantId) ? current.filter(id => id !== tenantId) : [...current, tenantId],
    );

  const clearTenants = () => setSelectedTenantIds([]);

  const toggleCategory = (categoryId: string) =>
    setSelectedCategoryIds(current =>
      current.includes(categoryId) ? current.filter(id => id !== categoryId) : [...current, categoryId],
    );

  const clearCategories = () => setSelectedCategoryIds([]);

  const clearAllFilters = () => {
    setSearch('');
    setStatusFilter(StatusFilter.All);
    setSelectedTenantIds([]);
    setSelectedCategoryIds([]);
  };

  const hasActiveFilters =
    statusFilter !== StatusFilter.All || selectedTenantIds.length > 0 || selectedCategoryIds.length > 0;

  return {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    statusCounts,
    selectedTenantIds,
    toggleTenant,
    clearTenants,
    tenantFilterOptions,
    tenantNameById,
    categories,
    selectedCategoryIds,
    toggleCategory,
    clearCategories,
    sortKey,
    sortDirection,
    toggleSort,
    rows,
    totalCount: allRows.length,
    filteredCount: filteredRows.length,
    hasActiveFilters,
    clearAllFilters,
    isTenantsLoading,
    isLoading: isTemplatesLoading,
    error,
  };
}
